import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import BookingFilters, { BookingFilters as BookingFiltersType, BookingFilterOptions } from "@/components/ui/BookingFilters";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Package, 
  Calendar, 
  Star,
  Download,
  Filter,
  Clock,
  MapPin,
  User
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { formatCurrency } from "@/lib/currency";
import { Seo } from "@/components/seo/Seo";
import TurnoverControlTower from "@/components/demand/TurnoverControlTower";

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'];

interface DashboardBooking {
  id: string;
  status: string | null;
  total_amount: number | null;
  scheduled_date: string | null;
  scheduled_time?: string | null;
  created_at?: string | null;
  company_id?: string | null;
  service_id?: string | null;
  services: { name: string | null } | null;
  companies: { company_name: string | null } | null;
}

interface DashboardFavourite {
  id: string;
  companies: { id: string; company_name: string | null; logo_url: string | null; slug: string | null } | null;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [timeRange, setTimeRange] = useState("6m");
  const [bookings, setBookings] = useState<DashboardBooking[]>([]);
  const [favourites, setFavourites] = useState<DashboardFavourite[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [resModal, setResModal] = useState<{ id: string | null, date: string, time: string, reason: string }>({ id: null, date: "", time: "", reason: "" });
  const [cancelModal, setCancelModal] = useState<{ id: string | null, reason: string }>({ id: null, reason: "" });
  const [bookingFilters, setBookingFilters] = useState<BookingFiltersType>({
    search: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const [bookingFilterOptions, setBookingFilterOptions] = useState<BookingFilterOptions>({
    companies: [],
    services: []
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { navigate('/auth'); return; }
        const prof = await supabase.from('profiles').select('id').eq('user_id', session.user.id).maybeSingle();
        const customerId = prof.data?.id;
        if (!customerId) { setBookings([]); setFavourites([]); return; }
        const rangeMonths = timeRange === '1m' ? 1 : timeRange === '3m' ? 3 : timeRange === '6m' ? 6 : 12;
        const since = new Date(); since.setMonth(since.getMonth() - rangeMonths);
        const { data: bks } = await supabase
          .from('bookings')
          .select('id, status, total_amount, created_at, company_id, service_id, scheduled_date, scheduled_time, services(name), companies(company_name)')
          .eq('customer_id', customerId)
          .gte('created_at', since.toISOString())
          .order('scheduled_date', { ascending: false })
          .order('scheduled_time', { ascending: false });
        const bookingsData = bks || [];
        setBookings(bookingsData as unknown as DashboardBooking[]);
        
        // Load filter options
        const companyMap = new Map<string, string>();
        bookingsData.forEach(b => {
          if (b.company_id && b.companies?.company_name) companyMap.set(b.company_id, b.companies.company_name);
        });
        const companies = Array.from(companyMap, ([id, name]) => ({
          id,
          name,
          slug: name.toLowerCase().replace(/\s+/g, '-')
        }));

        const serviceMap = new Map<string, string>();
        bookingsData.forEach(b => {
          if (b.service_id && b.services?.name) serviceMap.set(b.service_id, b.services.name);
        });
        const services = Array.from(serviceMap, ([id, name]) => ({ id, name }));

        setBookingFilterOptions({ companies, services });

        const { data: fav } = await supabaseUntyped
          .from('favourites')
          .select('id, companies!inner(id, company_name, logo_url, slug)')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });
        setFavourites((fav || []) as DashboardFavourite[]);
      } finally { setLoading(false); }
    })();
  }, [timeRange, navigate]);

  const monthlySpending = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of bookings) {
      const d = new Date(b.scheduled_date || b.created_at || '');
      const key = d.toLocaleString('en-US', { month: 'short' });
      const amt = Number(b.total_amount || 0);
      map[key] = (map[key] || 0) + amt;
    }
    return Object.keys(map).map(m => ({ month: m, amount: Math.round(map[m]) }));
  }, [bookings]);

  const serviceBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of bookings) {
      const name = b.services?.name || 'Other';
      const amt = Number(b.total_amount || 0);
      map[name] = (map[name] || 0) + amt;
    }
    return Object.keys(map).map((name, i) => ({ name, value: Math.round(map[name]), color: COLORS[i % COLORS.length] }));
  }, [bookings]);

  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Apply search filter
    if (bookingFilters.search) {
      const searchLower = bookingFilters.search.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.companies?.company_name?.toLowerCase().includes(searchLower) ||
        booking.services?.name?.toLowerCase().includes(searchLower) ||
        booking.status?.toLowerCase().includes(searchLower)
      );
    }

    // Apply company filter (by real company_id)
    if (bookingFilters.companyId) {
      filtered = filtered.filter(booking => booking.company_id === bookingFilters.companyId);
    }

    // Apply service filter (by real service_id)
    if (bookingFilters.serviceId) {
      filtered = filtered.filter(booking => booking.service_id === bookingFilters.serviceId);
    }

    // Apply status filter
    if (bookingFilters.status) {
      filtered = filtered.filter(booking => booking.status === bookingFilters.status);
    }

    // Apply date filter
    if (bookingFilters.dateFrom || bookingFilters.dateTo) {
      // Compare as yyyy-MM-dd strings to avoid timezone drift (the Date inputs are
      // UTC-midnight, so toISOString().slice(0,10) round-trips the picked day).
      const from = bookingFilters.dateFrom ? bookingFilters.dateFrom.toISOString().slice(0, 10) : null;
      const to = bookingFilters.dateTo ? bookingFilters.dateTo.toISOString().slice(0, 10) : null;
      filtered = filtered.filter(booking => {
        const d = String(booking.scheduled_date || '').slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (bookingFilters.sortBy) {
        case 'date': {
          const dateA = new Date(`${a.scheduled_date}T${a.scheduled_time || '00:00'}`);
          const dateB = new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`);
          comparison = dateA.getTime() - dateB.getTime();
          break;
        }
        case 'amount':
          comparison = (a.total_amount || 0) - (b.total_amount || 0);
          break;
        case 'created':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
      }

      // Stable tiebreaker so equal keys don't reorder between renders.
      if (comparison === 0) {
        comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        if (comparison === 0) comparison = String(a.id).localeCompare(String(b.id));
      }
      return bookingFilters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [bookings, bookingFilters]);

  const recentActivity = useMemo(() => {
    return filteredBookings.slice(0, 10).map((b) => ({
      date: String(b.scheduled_date || '').slice(0,10),
      service: b.services?.name || 'Service',
      company: b.companies?.company_name || 'Company',
      amount: Math.round(Number(b.total_amount || 0)),
      status: String(b.status)
    }));
  }, [filteredBookings]);

  const favoriteCompanies = useMemo(() => {
    return (favourites || []).map((f) => ({
      name: f.companies?.company_name || 'Company',
      slug: f.companies?.slug || f.companies?.id,
      bookings: (bookings || []).filter((b) => b.companies?.company_name === f.companies?.company_name).length,
      rating: 0,
      totalSpent: Math.round((bookings || []).filter((b) => b.companies?.company_name === f.companies?.company_name).reduce((s: number, b) => s + Number(b.total_amount || 0), 0))
    }));
  }, [favourites, bookings]);

  const openReschedule = (id: string) => setResModal({ id, date: "", time: "", reason: "" });
  const openCancel = (id: string) => setCancelModal({ id, reason: "" });

  const handleReschedule = async () => {
    if (!resModal.id) return;
    try {
      if (!resModal.date || !resModal.time || !resModal.reason) return;
      const { error } = await supabaseUntyped.rpc('customer_reschedule_booking', { p_booking_id: resModal.id, p_new_date: resModal.date, p_new_time: resModal.time, p_reason: resModal.reason });
      if (error) throw error;
      setResModal({ id: null, date: "", time: "", reason: "" });
      setBookings(prev => prev.map((b) => b.id === resModal.id ? { ...b, scheduled_date: resModal.date, scheduled_time: resModal.time } : b));
      
      // Calendar sync for customer reschedule (Dashboard)
      try {
        console.log('Calling calendar sync for customer reschedule (Dashboard):', resModal.id);
        const response = await fetch("/api/calendar/sync-booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: resModal.id })
        });
        const result = await response.json();
        console.log('Calendar sync response (customer reschedule Dashboard):', result);
      } catch (error) {
        console.error('Calendar sync error (customer reschedule Dashboard):', error);
      }
    } catch { /* non-blocking */ }
  };

  const handleCancel = async () => {
    if (!cancelModal.id) return;
    try {
      if (!cancelModal.reason) return;
      // Cancel + automatic refund handled server-side (48h policy, Stripe refund).
      // See api/ops.mjs:customer_cancel_booking.
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('not_authenticated');
      const cancelId = cancelModal.id;
      const resp = await fetch('/api/ops?action=customer_cancel_booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ booking_id: cancelId, reason: cancelModal.reason, access_token: accessToken })
      });
      const result = await resp.json().catch(() => ({} as { ok?: boolean; error?: string }));
      if (!resp.ok || !result?.ok) throw new Error(result?.error || 'cancel_failed');
      setCancelModal({ id: null, reason: "" });
      setBookings(prev => prev.map((b) => b.id === cancelId ? { ...b, status: 'cancelled' } : b));
      
      // Calendar sync for customer cancel (Dashboard)
      try {
        console.log('Calling calendar sync for customer cancel (Dashboard):', cancelModal.id);
        const response = await fetch("/api/calendar/sync-booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: cancelModal.id })
        });
        const result = await response.json();
        console.log('Calendar sync response (customer cancel Dashboard):', result);
      } catch (error) {
        console.error('Calendar sync error (customer cancel Dashboard):', error);
      }
    } catch { /* non-blocking */ }
  };

  const totalSpent = (bookings || []).filter((b) => String(b.status) === 'completed').reduce((sum: number, b) => sum + Number(b.total_amount || 0), 0);
  const totalOrders = (bookings || []).length || 1;
  const averageOrderValue = Math.round(totalSpent / totalOrders);
  const loyaltyPoints = 0;

  return (
    <div key={lang} className="min-h-screen app-page">
      <Seo title={t("dash.seo.title")} noindex canonicalPath="/dashboard" />
      {/* Header */}
      <div className="app-hero py-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between min-w-0">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 break-words">{t('dash.welcome')}</h1>
              <p className="text-white/90 break-words">{t('dash.subtitle')}</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-2 sm:gap-4 w-full sm:w-auto">
              <LanguageSwitcher compact />
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-full sm:w-32 bg-white/10 border-white/20 text-white min-w-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1m">{t('dash.range.1m')}</SelectItem>
                  <SelectItem value="3m">{t('dash.range.3m')}</SelectItem>
                  <SelectItem value="6m">{t('dash.range.6m')}</SelectItem>
                  <SelectItem value="1y">{t('dash.range.1y')}</SelectItem>
                </SelectContent>
              </Select>
              {/* Advanced Analytics button removed for customers */}
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-full sm:w-auto" onClick={() => {
                const rows = (bookings || []).map((b) => ({
                  date: String(b.scheduled_date || '').slice(0,10),
                  service: b.services?.name,
                  company: b.companies?.company_name,
                  amount: Number(b.total_amount || 0),
                  status: String(b.status)
                }));
                const csv = ['date,service,company,amount,status', ...rows.map(r => `${r.date},${r.service},${r.company},${r.amount},${r.status}`)].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'bookings.csv'; a.click(); URL.revokeObjectURL(url);
              }}>
                <Download className="h-4 w-4 mr-2" />
                {t('dash.export_csv')}
              </Button>
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-full sm:w-auto" onClick={() => {
                const out = (bookings || []).map((b) => ({
                  id: b.id,
                  date: String(b.scheduled_date || '').slice(0,10),
                  time: b.scheduled_time || null,
                  service: b.services?.name,
                  company: b.companies?.company_name,
                  amount: Number(b.total_amount || 0),
                  status: String(b.status)
                }));
                const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'bookings.json'; a.click(); URL.revokeObjectURL(url);
              }}>
                <Download className="h-4 w-4 mr-2" />
                {t('dash.export_json')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="app-dark-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-300">{t('dash.kpi.total_spent')}</CardTitle>
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-sky-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{formatCurrency(totalSpent)}</div>
              <div className="flex items-center text-sm mt-1">
                <TrendingUp className="h-4 w-4 text-accent-green-500 mr-1" />
                <span className="text-accent-green-500">+12.5%</span>
                <span className="text-slate-400 ml-1">{t('dash.kpi.from_last_month')}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="app-dark-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-300">{t('dash.kpi.total_orders')}</CardTitle>
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <Package className="h-4 w-4 text-emerald-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{totalOrders}</div>
              <div className="flex items-center text-sm mt-1">
                <TrendingUp className="h-4 w-4 text-accent-green-500 mr-1" />
                <span className="text-accent-green-500">+8.2%</span>
                <span className="text-slate-400 ml-1">{t('dash.kpi.from_last_month')}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="app-dark-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-300">{t('dash.kpi.aov')}</CardTitle>
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-amber-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{formatCurrency(averageOrderValue)}</div>
              <div className="flex items-center text-sm mt-1">
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-red-500">-2.1%</span>
                <span className="text-slate-400 ml-1">{t('dash.kpi.from_last_month')}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="app-dark-panel hover:shadow-strong">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-300">{t('dash.kpi.loyalty')}</CardTitle>
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <Star className="h-4 w-4 text-violet-300" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{loyaltyPoints}</div>
              <div className="flex items-center text-sm mt-1">
                <span className="text-slate-400">{t('dash.kpi.points_hint')}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="app-panel">
          <Tabs defaultValue="overview" className="w-full">
            <div className="border-b border-gray-200 px-6">
              <TabsList className="flex w-full gap-2 bg-transparent py-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <TabsTrigger 
                  value="overview" 
                  className="whitespace-nowrap shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm rounded-xl"
                >
                  {t('nav.overview')}
                </TabsTrigger>
                <TabsTrigger 
                  value="bookings" 
                  className="whitespace-nowrap shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm rounded-xl"
                >
                  {t('nav.bookings')}
                </TabsTrigger>
                <TabsTrigger
                  value="control-tower"
                  className="whitespace-nowrap shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm rounded-xl"
                >
                  Control Tower
                </TabsTrigger>
                <TabsTrigger
                  value="favorites"
                  className="whitespace-nowrap shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm rounded-xl"
                >
                  {t('nav.favorites')}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6">
              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Spending Trend */}
                  <Card className="border-gray-200 shadow-card">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('dash.spending_trend')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={monthlySpending}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="month" 
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <YAxis 
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px',
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="amount" 
                            stroke="#3b82f6" 
                            strokeWidth={3}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Service Breakdown */}
                  <Card className="border-gray-200 shadow-card">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('dash.service_breakdown')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {serviceBreakdown.length === 0 ? (
                        <div className="flex items-center justify-center h-[300px] text-sm text-gray-500">
                          {t('dash.service_breakdown.empty')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={serviceBreakdown}
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={95}
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                                stroke="#ffffff"
                                strokeWidth={2}
                                label={({ percent }) => (percent && percent > 0.07 ? `${Math.round(percent * 100)}%` : '')}
                                labelLine={false}
                              >
                                {serviceBreakdown.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value: number, name: string) => [formatCurrency(value), name]}
                                contentStyle={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '12px',
                                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <ul className="space-y-2 md:max-w-[220px] md:pr-2">
                            {serviceBreakdown
                              .slice()
                              .sort((a, b) => b.value - a.value)
                              .map((entry) => (
                                <li key={entry.name} className="flex items-start gap-2 text-sm">
                                  <span
                                    aria-hidden
                                    className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-gray-900 break-words leading-snug" title={entry.name}>
                                      {entry.name}
                                    </div>
                                    <div className="text-gray-500">{formatCurrency(entry.value)}</div>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="bookings" className="space-y-6">
                {/* Filters */}
                <Card className="border-gray-200 shadow-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-900">Filter Service History</CardTitle>
                  </CardHeader>
                  <CardContent className="max-w-full overflow-x-hidden">
                    <BookingFilters
                      filters={bookingFilters}
                      onFiltersChange={setBookingFilters}
                      options={bookingFilterOptions}
                      showCompanyFilter={true}
                      showServiceFilter={true}
                      showStatusFilter={true}
                      showDateFilter={true}
                      showSorting={true}
                    />
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="border-gray-200 shadow-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-900">
                      {t('dash.recent_history')} ({filteredBookings.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {recentActivity.length === 0 ? (
                        <div className="text-sm text-gray-600 text-center py-8">
                          {bookings.length === 0 ? 'No service history yet.' : 'No bookings match your filters.'}
                        </div>
                      ) : (
                        recentActivity.map((activity, index) => (
                          <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900">{activity.service}</h3>
                              <p className="text-sm text-gray-600">{activity.company}</p>
                              <p className="text-xs text-gray-500">{activity.date}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">{formatCurrency(activity.amount)}</p>
                              <span className={`text-xs px-2 py-1 rounded-lg ${
                                activity.status === 'completed' 
                                  ? 'bg-accent-green-500/20 text-accent-green-700' 
                                  : 'bg-accent-orange-500/20 text-accent-orange-700'
                              }`}>
                                {activity.status}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Reschedule/Cancel Modals */}
                {resModal.id && (
                  <div className="app-modal-overlay">
                    <div className="app-modal-panel w-full max-w-md p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Reschedule Booking</h3>
                        <button onClick={() => setResModal({ id: null, date: '', time: '', reason: '' })} className="text-gray-500 hover:text-gray-700">✕</button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label>New Date</Label>
                          <Input type="date" value={resModal.date} onChange={(e) => setResModal(prev => ({ ...prev, date: e.target.value }))} />
                        </div>
                        <div>
                          <Label>New Time</Label>
                          <Input type="time" value={resModal.time} onChange={(e) => setResModal(prev => ({ ...prev, time: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Reason</Label>
                          <Textarea value={resModal.reason} onChange={(e) => setResModal(prev => ({ ...prev, reason: e.target.value }))} />
                        </div>
                        <p className="text-xs text-gray-500">Cancellando oltre 48h prima dell'appuntamento ricevi il rimborso totale. Entro 48h viene trattenuta la commissione di servizio (e i costi di transazione) e ti viene rimborsato il resto. L'importo esatto è calcolato da Stripe.</p>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" className="border-gray-300" onClick={() => setResModal({ id: null, date: '', time: '', reason: '' })}>Cancel</Button>
                        <Button onClick={handleReschedule}>Save</Button>
                      </div>
                    </div>
                  </div>
                )}
                {cancelModal.id && (
                  <div className="app-modal-overlay">
                    <div className="app-modal-panel w-full max-w-md p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Cancel Booking</h3>
                        <button onClick={() => setCancelModal({ id: null, reason: '' })} className="text-gray-500 hover:text-gray-700">✕</button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label>Reason</Label>
                          <Textarea value={cancelModal.reason} onChange={(e) => setCancelModal(prev => ({ ...prev, reason: e.target.value }))} />
                        </div>
                        <p className="text-xs text-gray-500">Cancellando oltre 48h prima dell'appuntamento ricevi il rimborso totale. Entro 48h viene trattenuta la commissione di servizio (e i costi di transazione) e ti viene rimborsato il resto. L'importo esatto è calcolato da Stripe.</p>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" className="border-gray-300" onClick={() => setCancelModal({ id: null, reason: '' })}>Back</Button>
                        <Button variant="destructive" onClick={handleCancel}>Confirm Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="control-tower" className="space-y-6">
                <TurnoverControlTower />
              </TabsContent>

              <TabsContent value="favorites" className="space-y-6">
                {/* Favorite Companies */}
                <Card className="border-gray-200 shadow-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-900">{t('dash.favorite_companies')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {favoriteCompanies.map((company, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{company.name}</h3>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-sm text-gray-600">{company.bookings} bookings</span>
                              <div className="flex items-center gap-1">
                                <Star className="h-3 w-3 fill-current text-accent-orange-500" />
                                <span className="text-sm text-gray-900">{company.rating}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/c/${company.slug}`)}>Open</Button>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">{formatCurrency(company.totalSpent)}</p>
                              <p className="text-xs text-gray-500">total spent</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;